import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

export interface EmailMessage {
  id: string;
  subject: string;
  previewText: string;
  body: string;
  ctaText: string;
  ctaUrl?: string;
  dayOffset: number; // days after sequence start (0 = immediate)
}

export type SequenceType = "onboarding" | "nurture" | "reengagement";

export interface EmailSequences {
  onboarding: EmailMessage[]; // 5-7 emails — welcome & activate
  nurture: EmailMessage[];    // 3-5 emails — educate & build trust
  reengagement: EmailMessage[]; // 2-3 emails — win back churned users
  generatedAt?: string;
}

interface UseEmailSequenceResult {
  isLoading: boolean;
  error: string | null;
  sequences: EmailSequences | null;
  generateSequences: (projectId: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Hook for AI-powered email sequence generation.
 * Calls the `generateEmailSequence` Cloud Function with the projectId.
 * Returns three sequences: onboarding, nurture, and re-engagement.
 */
export function useEmailSequence(): UseEmailSequenceResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sequences, setSequences] = useState<EmailSequences | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const reset = useCallback(() => {
    setError(null);
    setSequences(null);
  }, []);

  const generateSequences = useCallback(async (projectId: string): Promise<void> => {
    if (!projectId) {
      setError("Project ID is required");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const generateFn = httpsCallable<
        { projectId: string },
        { success: boolean; sequences: EmailSequences }
      >(functions, "generateEmailSequence");

      const result = await generateFn({ projectId });
      setSequences(result.data.sequences);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to generate email sequences";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, sequences, generateSequences, clearError, reset };
}
